import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { API_ENDPOINTS } from '../config/api'
import { Button, Card, Badge, Alert } from '../components/ui'
import QuestionRenderer from '../components/QuestionRenderer'
import ResultVisualization from '../components/results/ResultVisualization'
import { resolveColor } from '../styles/colorContrast'
import type { GroupResult } from '../types'

// ---------- Types ----------

interface QuestionGroup {
  id: string
  name: string
  order: number
  color: string
  tint?: string
  description?: string
}

interface Question {
  id: string
  text: string
  type: 'BOOLEAN' | 'TEXT' | 'SINGLE_CHOICE' | 'MULTIPLE_CHOICE' | 'RATING' | 'COUNTRY'
  required: boolean
  order: number
  options: string[]
  group_id?: string
  weight?: number
  rating_labels?: Record<string, string>
  scoring_config?: {
    type: string
    min_value?: number
    max_value?: number
    true_value?: number
    false_value?: number
    option_weights?: Record<string, number>
  }
}

interface Survey {
  id: string
  title: string
  description: string
  result_type?: string
  question_groups?: QuestionGroup[]
  questions: Question[]
}

// ---------- Client-side scoring ----------

function calculateClientScore(question: Question, response: any): { score: number; maxScore: number } {
  const config = question.scoring_config
  if (!config || !config.type) return { score: 0, maxScore: 0 }
  const weight = question.weight ?? 1

  switch (config.type) {
    case 'direct_scale': {
      const maxVal = config.max_value ?? 5
      const val = typeof response === 'number' ? response : 0
      return { score: val * weight, maxScore: maxVal * weight }
    }
    case 'binary': {
      const trueVal = config.true_value ?? 10
      const falseVal = config.false_value ?? 0
      const maxVal = Math.max(trueVal, falseVal)
      const val = response === true ? trueVal : response === false ? falseVal : 0
      return { score: val * weight, maxScore: maxVal * weight }
    }
    case 'weighted_options': {
      const weights = config.option_weights || {}
      const maxWeight = Object.values(weights).length > 0
        ? Math.max(...Object.values(weights))
        : 0
      const val = typeof response === 'string' && weights[response] !== undefined
        ? weights[response]
        : 0
      return { score: val * weight, maxScore: maxWeight * weight }
    }
    case 'qualitative':
      return { score: 0, maxScore: 0 }
    default:
      return { score: 0, maxScore: 0 }
  }
}

function computeGroupResults(
  questions: Question[],
  groups: QuestionGroup[],
  responses: Record<string, any>
): { groupResults: GroupResult[]; overallPercentage: number } {
  const sortedGroups = [...groups].sort((a, b) => a.order - b.order)

  const groupResults: GroupResult[] = sortedGroups.map((g) => {
    const gQuestions = questions.filter((q) => q.group_id === g.id)
    const questionScores = gQuestions.map((q) => {
      const { score, maxScore } = calculateClientScore(q, responses[q.id])
      return {
        question_id: q.id,
        category: q.text,
        score,
        max_possible_score: maxScore,
        percentage: maxScore > 0 ? (score / maxScore) * 100 : 0,
      }
    })

    const totalScore = questionScores.reduce((sum, qs) => sum + qs.score, 0)
    const maxPossible = questionScores.reduce((sum, qs) => sum + qs.max_possible_score, 0)

    return {
      group_id: g.id,
      group_name: g.name,
      color: g.color,
      tint: g.tint,
      total_score: totalScore,
      max_possible_score: maxPossible,
      percentage: maxPossible > 0 ? (totalScore / maxPossible) * 100 : 0,
      question_scores: questionScores,
    }
  })

  const overallScore = groupResults.reduce((sum, g) => sum + g.total_score, 0)
  const overallMax = groupResults.reduce((sum, g) => sum + g.max_possible_score, 0)
  const overallPercentage = overallMax > 0 ? (overallScore / overallMax) * 100 : 0

  return { groupResults, overallPercentage }
}

// ---------- Component ----------

const AdminTestPage: React.FC = () => {
  useAuth()
  const navigate = useNavigate()
  const { surveyId = '' } = useParams<{ surveyId: string }>()

  const [survey, setSurvey] = useState<Survey | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [step, setStep] = useState<'taking' | 'results'>('taking')
  const [currentGroupIndex, setCurrentGroupIndex] = useState(0)
  const [responses, setResponses] = useState<Record<string, any>>({})

  // Load survey via preview endpoint
  useEffect(() => {
    const fetchSurvey = async () => {
      try {
        const token = localStorage.getItem('access_token')
        const res = await fetch(`${API_ENDPOINTS.surveys.byId(surveyId)}/preview`, {
          headers: { Authorization: token ? `Bearer ${token}` : '' },
        })
        if (!res.ok) {
          setError(res.status === 403 ? 'You need OWNER or ADMIN access to test this survey.' : 'Survey not found')
          return
        }
        const data = await res.json()
        setSurvey(data)

        // Initialize responses
        const initial: Record<string, any> = {}
        data.questions?.forEach((q: Question) => {
          if (q.type === 'BOOLEAN') initial[q.id] = null
          else if (q.type === 'RATING') initial[q.id] = null
          else initial[q.id] = ''
        })
        setResponses(initial)
      } catch (err) {
        console.error('Error loading survey preview:', err)
        setError('Failed to load survey')
      } finally {
        setLoading(false)
      }
    }
    fetchSurvey()
  }, [surveyId])

  // ---------- Computed ----------

  const questions = survey?.questions || []
  const groups = survey?.question_groups || []
  const isGrouped = groups.length > 0
  const sortedGroups = [...groups].sort((a, b) => a.order - b.order)

  const pages: Array<{ group: QuestionGroup | null; questions: Question[] }> = (() => {
    if (!isGrouped) {
      return questions.map((q) => ({ group: null, questions: [q] }))
    }
    const result: Array<{ group: QuestionGroup | null; questions: Question[] }> = []
    for (const g of sortedGroups) {
      const gq = questions.filter((q) => q.group_id === g.id).sort((a, b) => a.order - b.order)
      if (gq.length > 0) result.push({ group: g, questions: gq })
    }
    const ungrouped = questions.filter((q) => !q.group_id)
    for (const q of ungrouped) result.push({ group: null, questions: [q] })
    return result
  })()

  const totalPages = pages.length
  const currentPage = currentGroupIndex < totalPages ? pages[currentGroupIndex] : null
  const currentGroup = currentPage?.group || null
  const currentGroupColor = currentGroup?.color || null
  const isLastPage = currentGroupIndex === totalPages - 1

  const answeredCount = Object.values(responses).filter(
    (v) => v !== null && v !== undefined && v !== ''
  ).length
  const overallProgress = questions.length > 0
    ? (answeredCount / questions.length) * 100
    : 0

  const answeredOnPage = currentPage
    ? currentPage.questions.filter((q) => {
        const v = responses[q.id]
        return v !== null && v !== undefined && v !== ''
      }).length
    : 0

  // ---------- Handlers ----------

  const handleResponseChange = (questionId: string, value: any) => {
    setResponses((prev) => ({ ...prev, [questionId]: value }))
  }

  const handleNext = () => {
    if (isLastPage) {
      setStep('results')
    } else {
      setCurrentGroupIndex((prev) => prev + 1)
      window.scrollTo(0, 0)
    }
  }

  const handlePrevious = () => {
    if (currentGroupIndex > 0) {
      setCurrentGroupIndex((prev) => prev - 1)
      window.scrollTo(0, 0)
    }
  }

  const handleReset = () => {
    const initial: Record<string, any> = {}
    questions.forEach((q) => {
      if (q.type === 'BOOLEAN') initial[q.id] = null
      else if (q.type === 'RATING') initial[q.id] = null
      else initial[q.id] = ''
    })
    setResponses(initial)
    setCurrentGroupIndex(0)
    setStep('taking')
    window.scrollTo(0, 0)
  }

  // ---------- Render ----------

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-foreground-muted">Loading survey preview...</div>
      </div>
    )
  }

  if (error || !survey) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-error mb-4">{error || 'Survey not found'}</p>
          <Button variant="primary" onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </div>
    )
  }

  // ---- RESULTS STEP ----
  if (step === 'results') {
    const hasGroups = isGrouped && sortedGroups.length > 0
    const { groupResults, overallPercentage } = hasGroups
      ? computeGroupResults(questions, sortedGroups, responses)
      : { groupResults: [], overallPercentage: 0 }

    // Flat score for non-grouped
    let flatTotal = 0
    let flatMax = 0
    if (!hasGroups) {
      questions.forEach((q) => {
        const { score, maxScore } = calculateClientScore(q, responses[q.id])
        flatTotal += score
        flatMax += maxScore
      })
    }
    const displayPercentage = hasGroups ? overallPercentage : (flatMax > 0 ? (flatTotal / flatMax) * 100 : 0)
    const displayScore = hasGroups
      ? groupResults.reduce((s, g) => s + g.total_score, 0)
      : flatTotal
    const displayMax = hasGroups
      ? groupResults.reduce((s, g) => s + g.max_possible_score, 0)
      : flatMax

    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-3xl mx-auto px-4 py-8">
          {/* Warning banner */}
          <Alert variant="warning" className="mb-4">
            These are test results only. Nothing has been saved to the database.
          </Alert>

          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-foreground">{survey.title}</h1>
              <Badge variant="warning">TEST</Badge>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleReset}>Test Again</Button>
              <Button variant="primary" size="sm" onClick={() => navigate(`/surveys/${surveyId}/edit`)}>
                Back to Builder
              </Button>
            </div>
          </div>

          {/* Overall Score */}
          <Card className="mb-6">
            <div className="flex flex-col items-center py-4">
              <span className="text-xs text-foreground-muted uppercase tracking-wide">Overall Score</span>
              <span className="text-4xl font-bold text-foreground">{Math.round(displayPercentage)}%</span>
              <span className="text-sm text-foreground-muted">{Math.round(displayScore)} / {Math.round(displayMax)} points</span>
            </div>
          </Card>

          {/* Group result visualization */}
          {hasGroups && (
            <Card className="mb-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">
                {survey.result_type === 'pyramid' ? 'Pyramid Visualization' : 'Results'}
              </h3>
              <ResultVisualization
                resultType={survey.result_type || 'breakdown'}
                groupResults={groupResults}
                overallPercentage={overallPercentage}
              />
            </Card>
          )}

          {/* Section Scores */}
          {hasGroups && (
            <Card className="mb-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">Section Scores</h3>
              <div className="flex flex-col gap-3">
                {groupResults.map((g) => (
                  <div key={g.group_id} className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: g.color }} />
                    <span className="text-sm flex-1 font-bold text-foreground">{g.group_name}</span>
                    <span className="text-sm text-foreground-muted">
                      {Math.round(g.total_score)}/{Math.round(g.max_possible_score)}
                    </span>
                    <div className="w-24 bg-border-light rounded-full h-2">
                      <div
                        className="h-2 rounded-full"
                        style={{ width: `${g.percentage}%`, backgroundColor: g.color }}
                      />
                    </div>
                    <span className="text-sm font-bold text-foreground w-10 text-right">
                      {Math.round(g.percentage)}%
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Per-question breakdown */}
          <Card className="mb-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Question Details</h3>
            <div className="flex flex-col gap-4">
              {hasGroups ? (
                sortedGroups.map((g) => {
                  const gQuestions = questions.filter((q) => q.group_id === g.id).sort((a, b) => a.order - b.order)
                  return (
                    <div key={g.id}>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: g.color }} />
                        <span className="text-sm font-bold text-foreground">{g.name}</span>
                      </div>
                      {gQuestions.map((q) => {
                        const val = responses[q.id]
                        const answered = val !== null && val !== undefined && val !== ''
                        const display = q.type === 'RATING' && typeof val === 'number'
                          ? `${val}/5`
                          : q.type === 'BOOLEAN' && val !== null
                            ? val ? 'Yes' : 'No'
                            : answered ? String(val) : null
                        return (
                          <div key={q.id} className="flex items-center gap-3 ml-5 py-1">
                            <span className="text-sm flex-1 text-foreground">{q.text}</span>
                            {answered ? (
                              <Badge variant="primary" size="sm">{display}</Badge>
                            ) : (
                              <Badge variant="neutral" size="sm">skipped</Badge>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )
                })
              ) : (
                questions.map((q) => {
                  const val = responses[q.id]
                  const answered = val !== null && val !== undefined && val !== ''
                  const display = q.type === 'RATING' && typeof val === 'number'
                    ? `${val}/5`
                    : q.type === 'BOOLEAN' && val !== null
                      ? val ? 'Yes' : 'No'
                      : answered ? String(val) : null
                  return (
                    <div key={q.id} className="flex items-center gap-3 py-1">
                      <span className="text-sm flex-1 text-foreground">{q.text}</span>
                      {answered ? (
                        <Badge variant="primary" size="sm">{display}</Badge>
                      ) : (
                        <Badge variant="neutral" size="sm">skipped</Badge>
                      )}
                    </div>
                  )
                })
              )}
            </div>
          </Card>

          {/* Footer actions */}
          <div className="flex gap-3">
            <Button variant="outline" onClick={handleReset}>Test Again</Button>
            <Button variant="primary" onClick={() => navigate(`/surveys/${surveyId}/edit`)}>
              Back to Builder
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // ---- TAKING STEP ----
  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Warning banner */}
      <div className="bg-warning-light border-b border-warning px-4 py-2">
        <div className="max-w-2xl mx-auto">
          <p className="text-sm text-warning font-medium">
            TEST MODE: You are taking this survey as an admin. No responses will be saved.
            This is exactly how respondents will see the survey.
          </p>
        </div>
      </div>

      {/* Header */}
      <div className="bg-surface shadow-sm">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <h1 className="text-sm font-semibold text-foreground">{survey.title}</h1>
              <Badge variant="warning" size="sm">TEST</Badge>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={handleReset}>Reset</Button>
              <Button variant="ghost" size="sm" onClick={() => navigate(`/surveys/${surveyId}/edit`)}>
                Back to Builder
              </Button>
            </div>
          </div>

          {/* Progress */}
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-foreground-muted">
              {isGrouped ? `Section ${currentGroupIndex + 1} of ${totalPages}` : `Question ${currentGroupIndex + 1} of ${totalPages}`}
            </span>
            <span className="text-sm text-foreground-muted">{Math.round(overallProgress)}%</span>
          </div>
          <div className="w-full bg-border-light rounded-full h-2">
            <div
              className="h-2 rounded-full transition-all duration-300"
              style={{
                width: `${overallProgress}%`,
                backgroundColor: currentGroupColor ? resolveColor(currentGroupColor) : 'var(--color-primary, #6366F1)',
              }}
            />
          </div>
          {isGrouped && pages.length > 1 && (
            <div className="flex gap-1 mt-2 justify-center">
              {pages.map((page, i) => (
                <div
                  key={i}
                  className="h-2 rounded-sm"
                  style={{
                    width: `${100 / pages.length}%`,
                    backgroundColor: i <= currentGroupIndex
                      ? (page.group?.color ? resolveColor(page.group.color) : 'var(--color-primary, #6366F1)')
                      : 'var(--theme-secondary, #e5e7eb)',
                    opacity: i === currentGroupIndex ? 1 : 0.5,
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Page content */}
      <div className="flex-1">
        <div className="max-w-3xl mx-auto px-4 py-8 w-full">
          {/* Group header */}
          {currentGroup && (
            <div className="bg-surface rounded-xl shadow-sm border border-border-light p-5 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 rounded-sm" style={{ backgroundColor: currentGroupColor ? resolveColor(currentGroupColor) : '#6B7280' }} />
                <h2 className="text-xl font-bold text-foreground">{currentGroup.name}</h2>
              </div>
              {currentGroup.description && (
                <p className="text-sm text-foreground-muted mt-1 ml-7">{currentGroup.description}</p>
              )}
              <p className="text-xs text-foreground-muted mt-2 ml-7">
                {answeredOnPage} of {currentPage!.questions.length} answered
              </p>
            </div>
          )}

          {/* Questions */}
          <div className="space-y-4">
            {currentPage?.questions.map((q) => (
              <div key={q.id} className="bg-surface rounded-xl shadow-sm border border-border-light p-6">
                <div className="mb-4">
                  {!isGrouped && (
                    <p className="text-sm font-medium text-primary mb-2">
                      Question {currentGroupIndex + 1}
                    </p>
                  )}
                  <h3 className={`${isGrouped ? 'text-lg' : 'text-2xl'} font-bold text-foreground`}>
                    {q.text}
                    {q.required && <span className="text-error ml-1">*</span>}
                  </h3>
                </div>
                <QuestionRenderer
                  question={q}
                  value={responses[q.id]}
                  onChange={(value) => handleResponseChange(q.id, value)}
                  ratingLabels={q.rating_labels}
                  groupColor={currentGroupColor || undefined}
                />
              </div>
            ))}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6">
            <button
              type="button"
              onClick={handlePrevious}
              className={`flex items-center px-5 py-3 text-sm font-medium rounded-lg transition-colors
                ${currentGroupIndex === 0
                  ? 'text-foreground-muted cursor-not-allowed'
                  : 'text-foreground hover:bg-surface hover:shadow-sm border border-transparent hover:border-border-light'
                }
              `}
              disabled={currentGroupIndex === 0}
            >
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {isGrouped ? 'Previous Section' : 'Previous'}
            </button>

            <button
              type="button"
              onClick={handleNext}
              className="flex items-center px-6 py-3 text-sm font-semibold rounded-lg bg-primary text-white hover:bg-primary-dark transition-colors"
            >
              {isLastPage ? (
                <>
                  View Results
                  <svg className="w-4 h-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </>
              ) : (
                <>
                  {isGrouped ? 'Next Section' : 'Next'}
                  <svg className="w-4 h-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminTestPage
